// Map view: the family's places, pinned.
//
// The tree answers who, the timeline answers when, and until this existed
// nothing answered WHERE - which in a Flemish family record is the most
// identifying fact there is. Six forenames recur for two hundred years, so
// "born in Klerken" is what separates one Jérôme Gunst from another (see
// AddPlacesToFamilyTreePeople). Read as a map rather than as a column of
// strings, the same data says something the table cannot: that a family sat in
// three villages within ten kilometres for a century and then one branch turned
// up in San Francisco.
//
// WHERE THE DATA COMES FROM. The events are read out of the payload every other
// layout already has - birth_place / death_place / burial_place on a node,
// marriage_place on a partner edge. The COORDINATES come from a second, small
// fetch (placesUrl -> FamilyTrees::MapPlaces), because a free-text place has
// none: they are resolved off-cycle by a geocoder and arrive minutes later.
// Splitting it that way is also what keeps the privacy scrub in one place - a
// place this reader is not allowed to see never reaches the payload, so it can
// never be pinned here no matter what the lookup contains.
//
// TWO MODES, because "where is my family" and "where did things happen" are
// different questions and the second one answers the first badly. A person with
// a birthplace, a place of death and a grave is THREE pins in the event view -
// so a tree of 1,481 people reads as 160 scattered marks and you cannot tell
// whether a village holds forty relatives or one relative's whole life. People
// mode puts each person on the map exactly once, at their birthplace (or,
// failing that, wherever they are known to have been), and the number on a pin
// becomes a number of PEOPLE. Events mode is the original, and is what the kind
// chips belong to.
//
// A PLACE THAT DOES NOT RESOLVE IS NOT AN ERROR. "Clercken" is the 1830
// spelling of a village the gazetteer files under "Klerken", and the whole
// point of the free-text field is to record what the document says. Those land
// in the footer count rather than being silently dropped, so a reader can see
// the map is partial and why.
import L from "leaflet"
import { tileUrl, OSM_ATTRIBUTION } from "../utils/map_tiles"
import { csrfToken } from "../utils/csrf"
import { birthOf, deathOf, lifespanLabel, personOpenable, applyPersonLink } from "./genealogy"

// What kind of event a pin carries. Order is the order of a life, which is the
// order the chips and a place's list read in.
const KINDS = [
    { key: "birth", label: "Births", singular: "Born", icon: "bi-asterisk", color: "#2563eb" },
    { key: "marriage", label: "Marriages", singular: "Married", icon: "bi-suit-heart", color: "#db2777" },
    { key: "death", label: "Deaths", singular: "Died", icon: "bi-dot", color: "#475569" },
    { key: "burial", label: "Burials", singular: "Buried", icon: "bi-flower1", color: "#15803d" }
]
const KIND_BY_KEY = new Map(KINDS.map((kind) => [kind.key, kind]))

// Must reproduce PlaceLocation.normalize exactly, or a pin's coordinates never
// match the string that asked for them. Deliberately plain on both sides.
function normalizePlace(value) {
    return String(value || "").trim().replace(/\s+/g, " ").toLowerCase()
}

export function renderMap(ctx) {
    let { nodes, edges, nodesById, filterSlot, placesUrl } = ctx

    let events = collectEvents(nodes, edges, nodesById)
    let people = collectPeople(nodes)
    let wrap = document.createElement("div")
    wrap.className = "ft-map"

    if (!events.length) {
        wrap.appendChild(emptyState(ctx))
        return { element: wrap, panZoom: false }
    }

    let canvas = document.createElement("div")
    canvas.className = "ft-map-canvas"
    wrap.appendChild(canvas)

    let state = {
        // People first: it is the plainer read, and the one somebody opening a
        // map of their family is asking for.
        mode: "people",
        kinds: new Set(KINDS.map((kind) => kind.key)),
        journeys: false,
        coords: new Map(),
        pending: 0,
        // Strings the gazetteer answered and could not place. They draw nothing,
        // so without this list they are invisible AND unfixable.
        unlocated: [],
        editable: false,
        loaded: false
    }

    let panel = buildPanel({
        state: state,
        // Saving a correction changes where things ARE, so the map is re-read
        // and re-framed rather than patched in place. Re-framing matters:
        // somebody who has just moved a village from London to Flanders wants
        // to see where it went, and leaving the camera where it was hides the
        // one thing they did.
        onSaved: () => loadPlaces({ refit: true })
    })
    wrap.appendChild(panel.element)

    let footer = document.createElement("p")
    footer.className = "ft-map-footer"
    wrap.appendChild(footer)

    // Leaflet measures its container, so it cannot be built until this element
    // is in the document - the caller appends it after we return.
    //
    // SCHEDULED TWO WAYS ON PURPOSE. A frame is the natural moment, but
    // requestAnimationFrame DOES NOT FIRE in a hidden or backgrounded tab, and
    // "open this in a new tab" is an ordinary thing to do with a link to a
    // family tree. Waiting for a frame that never comes left the map a blank
    // grey box until the tab was focused, with no error to find. A timeout
    // fires either way; whichever arrives first wins, and the other is a no-op.
    let map = null
    let layers = null
    let resizes = null
    let cancelled = false
    let started = false
    let start = () => {
        if (started || cancelled || !canvas.isConnected) return

        started = true
        map = L.map(canvas, { zoomControl: true, worldCopyJump: true, scrollWheelZoom: true })
        L.tileLayer(tileUrl(), {
            maxZoom: 18,
            attribution: OSM_ATTRIBUTION
        }).addTo(map)
        map.setView([48, 4], 4)
        layers = { pins: L.layerGroup().addTo(map), journeys: L.layerGroup().addTo(map) }
        draw({ fit: true })
        loadPlaces()
        watchSize()
    }
    requestAnimationFrame(start)
    setTimeout(start, 0)

    // Leaflet caches the container's size, so anything that resizes it leaves
    // the map painting a grid for a box it no longer occupies - a grey band
    // along an edge with no tiles in it. In this shell that is not a corner
    // case: opening and closing the assistant panel changes the width on every
    // page, and the map is laid out one frame after its box is.
    function watchSize() {
        if (typeof ResizeObserver !== "function") return

        let pending = false
        resizes = new ResizeObserver(() => {
            if (pending || !map) return

            pending = true
            requestAnimationFrame(() => {
                pending = false
                // Re-checked inside the frame: a teardown between the two is
                // exactly what happens when a re-render swaps the layout out.
                if (!cancelled && map) map.invalidateSize()
            })
        })
        resizes.observe(canvas)
    }

    if (filterSlot) renderFilters(filterSlot, state, events, () => draw({ fit: true }))

    function loadPlaces({ refit = true } = {}) {
        if (!placesUrl) {
            state.loaded = true
            draw({ fit: true })
            return
        }
        panel.setUrl(placesUrl)
        fetch(placesUrl, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            .then((response) => (response.ok ? response.json() : null))
            .then((payload) => {
                if (cancelled || !payload) throw new Error("places unavailable")
                ;(payload.places || []).forEach((place) => {
                    if (place.lat == null || place.lon == null) return
                    state.coords.set(normalizePlace(place.query), {
                        lat: Number(place.lat), lon: Number(place.lon),
                        label: place.label || place.query,
                        // A state or a country, not a village - see
                        // PlaceLocation::SETTLEMENT_KINDS.
                        coarse: !!place.coarse,
                        // The only place in its country on this tree: drawn
                        // like any other and worth a second look. See
                        // FamilyTrees::MapPlaces#flag_uncertain.
                        uncertain: !!place.uncertain,
                        corrected: !!place.corrected
                    })
                })
                state.unlocated = Array.isArray(payload.unlocated) ? payload.unlocated : []
                state.editable = !!payload.editable
                state.pending = Number(payload.pending) || 0
                state.loaded = true
                draw({ fit: refit })
            })
            .catch(() => {
                if (cancelled) return

                state.loaded = true
                draw({ fit: true })
            })
    }

    function draw({ fit }) {
        if (!map) return

        layers.pins.clearLayers()
        layers.journeys.clearLayers()
        panel.close()

        let peopleMode = state.mode === "people"
        let shown = peopleMode ? people : events.filter((event) => state.kinds.has(event.kind))
        let places = groupByPlace(shown, state.coords)
        let bounds = []

        places.forEach((place) => {
            bounds.push([place.lat, place.lon])
            L.marker([place.lat, place.lon], { icon: pinIcon(place, peopleMode), title: `${place.label} · ${place.events.length}` })
                .addTo(layers.pins)
                .on("click", () => panel.open(place, peopleMode))
        })

        // A journey is a birth and a death, whatever the map is counting - so it
        // is always drawn from the full event list rather than from a people
        // view that holds one entry per person.
        if (state.journeys) drawJourneys(layers.journeys, peopleMode ? events : shown, state.coords)
        if (fit && bounds.length) map.fitBounds(bounds, { padding: [48, 48], maxZoom: 9 })
        // A container that was hidden or resized while another layout was up
        // measures as zero and paints one grey tile in the corner.
        map.invalidateSize()

        writeFooter(footer, {
            places, shown, state, publicView: ctx.publicView,
            totalEvents: events.length, totalPeople: nodes.length, placedPeople: people.length,
            onShowUnlocated: () => panel.openUnlocated()
        })
    }

    return {
        element: wrap,
        panZoom: false,
        // The stage is emptied with innerHTML on every re-render, which would
        // leave Leaflet's handlers bound to a detached node and its tile
        // requests in flight. See contact_tree_controller#_renderAlternate.
        cleanup() {
            cancelled = true
            resizes?.disconnect()
            resizes = null
            if (map) {
                map.remove()
                map = null
            }
        }
    }
}

// --- Events -----------------------------------------------------------------

// One row per thing that happened somewhere: a birth, a death, a burial, or a
// marriage. A person contributes up to three and a couple one, so the count on
// a pin is events rather than people - "47 things happened in Klerken" is the
// honest number, and it is also the one that makes the ancestral village
// obvious at a glance.
function collectEvents(nodes, edges, nodesById) {
    let events = []

    nodes.forEach((node) => {
        let birth = birthOf(node)
        let death = deathOf(node)
        push(events, node, "birth", node.birth_place, birth.known ? birth.year : null)
        push(events, node, "death", node.death_place, death.known ? death.year : null)
        // A burial has no date of its own in the schema; it reads under the
        // death year, which is the year a reader is looking for anyway.
        push(events, node, "burial", node.burial_place, death.known ? death.year : null)
    })

    ;(edges || []).forEach((edge) => {
        if (edge.kind !== "partner" || !edge.marriage_place) return

        let from = nodesById.get(edge.from)
        let to = nodesById.get(edge.to)
        if (!from && !to) return

        // The couple, not one of them: a marriage belongs to the pair, and
        // filing it under whichever id happened to be `from` would list it on
        // one card and not the other.
        events.push({
            kind: "marriage",
            place: edge.marriage_place,
            key: normalizePlace(edge.marriage_place),
            year: marriageYear(edge),
            people: [from, to].filter(Boolean),
            name: [from, to].filter(Boolean).map((person) => person.name).join(" & ")
        })
    })

    return events
}

// One entry per PERSON who is placeable at all, at the best place known for
// them. Birthplace first: it is the one a genealogical record almost always
// carries, and "where someone is from" is what a family map is read for. A
// person known only by where they died or were buried still belongs on the map
// - leaving them off would quietly under-count the very generations the tree
// exists to show - and the panel says which it is, so a dot never implies a
// birth nobody recorded.
function collectPeople(nodes) {
    let out = []
    nodes.forEach((node) => {
        let basis = placeBasis(node)
        if (!basis) return

        let birth = birthOf(node)
        let death = deathOf(node)
        out.push({
            kind: basis.kind,
            place: basis.place,
            key: normalizePlace(basis.place),
            year: birth.known ? birth.year : (death.known ? death.year : null),
            lifespan: lifespanLabel(node),
            people: [node],
            name: node.name
        })
    })
    return out
}

function placeBasis(node) {
    if (node.birth_place) return { kind: "birth", place: node.birth_place }
    if (node.death_place) return { kind: "death", place: node.death_place }
    if (node.burial_place) return { kind: "burial", place: node.burial_place }
    return null
}

function push(events, node, kind, place, year) {
    if (!place) return

    events.push({ kind: kind, place: place, key: normalizePlace(place), year: year, people: [node], name: node.name })
}

// The payload sends a marriage as partial-date fields (Genealogy::PartialDate),
// so a year-only marriage has no parseable date - read the year directly and
// fall back to the ISO date when there is one.
function marriageYear(edge) {
    if (edge.marriage_year != null) return Number(edge.marriage_year)
    if (!edge.marriage_date) return null

    let year = Number(String(edge.marriage_date).slice(0, 4))
    return Number.isFinite(year) ? year : null
}

// Events -> one pin per LOCATION. A place nobody could resolve drops out here
// and is counted in the footer instead.
//
// Grouped by where it is, not by how it was spelled. A register spells the same
// village three ways across two centuries - one real tree had "Meigem",
// "Meigem, Oost-Vlaanderen, België" and "Berg begraafplaats, Meigem,
// Oost-Vlaanderen, België" all resolving to the same point - and keying on the
// string drew three pins stacked on one village, each claiming to be the whole
// story of that place. Correcting a historical spelling onto its modern town
// does the same thing deliberately, so this is also what makes a fix LOOK like
// a fix.
// How close two coordinates have to be to count as the same place. Distinct
// settlements' centroids are kilometres apart; the same village reached by two
// routes is metres apart.
const SAME_PLACE_METRES = 250

function metresBetween(a, b) {
    let rad = Math.PI / 180
    let dLat = (b.lat - a.lat) * rad
    let dLon = (b.lon - a.lon) * rad
    let h = Math.sin(dLat / 2) ** 2 +
        Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2
    return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

function groupByPlace(events, coords) {
    let clusters = []
    events.forEach((event) => {
        let coord = coords.get(event.key)
        if (!coord) return

        // NEAREST CLUSTER, not a rounded grid cell. A grid looks like it works
        // and then puts two points 99m apart either side of a boundary: the
        // same Klerken, reached once through the bulk resolver and once through
        // the correction editor, drew as two pins with no precision that would
        // have merged them. Distance has no boundaries to straddle.
        let entry = clusters.find((cluster) => metresBetween(cluster, coord) <= SAME_PLACE_METRES)
        if (!entry) {
            entry = {
                key: event.key, label: coord.label || event.place, name: event.place,
                lat: coord.lat, lon: coord.lon, coarse: !!coord.coarse,
                uncertain: false, corrected: false,
                // Every spelling that landed here, so the editor can name them.
                spellings: [], events: []
            }
            clusters.push(entry)
        }
        if (!entry.spellings.some((spelling) => spelling.key === event.key)) {
            entry.spellings.push({ key: event.key, text: event.place })
            // One doubtful spelling makes the pin worth a look, and one fixed
            // spelling makes it a pin somebody has already placed.
            if (coord.uncertain) entry.uncertain = true
            if (coord.corrected) entry.corrected = true
        }
        entry.events.push(event)
    })
    return clusters.sort((a, b) => b.events.length - a.events.length)
}

// --- Drawing ----------------------------------------------------------------

// Sized by how much happened there, the same way the network map's city bubbles
// are: the ancestral village should be the biggest thing on screen without
// anybody having to read a number off it. Tinted by whichever kind of event
// dominates, so a churchyard reads differently from a birthplace.
const PEOPLE_COLOR = "#4f46e5"

function pinIcon(place, peopleMode) {
    let count = place.events.length
    let size = Math.min(54, 24 + Math.round(Math.sqrt(count) * 7))
    // In people mode every pin means the same thing - so many relatives - and
    // tinting by event kind would be saying something the mark is not about.
    let dominant = peopleMode ? null : dominantKind(place.events)
    let bubble = document.createElement("span")
    bubble.className = "ft-map-pin"
    // A region's coordinates are the middle of a country, not a place anybody
    // lived. Drawn hollow so it never reads as a village with a known location.
    if (place.coarse) bubble.classList.add("ft-map-pin--coarse")
    // Drawn with a query mark rather than hidden or moved: the map's job here
    // is to raise the question, and a pin that quietly disappeared would be a
    // worse lie than the wrong one.
    if (place.uncertain) bubble.classList.add("ft-map-pin--uncertain")
    bubble.style.width = `${size}px`
    bubble.style.height = `${size}px`
    bubble.style.background = dominant ? dominant.color : PEOPLE_COLOR
    bubble.textContent = count > 1 ? String(count) : ""
    if (count === 1) {
        let icon = document.createElement("i")
        icon.className = `bi ${peopleMode ? "bi-person-fill" : dominant.icon}`
        icon.setAttribute("aria-hidden", "true")
        bubble.appendChild(icon)
    }
    return L.divIcon({ className: "ft-map-pin-wrap", html: bubble.outerHTML, iconSize: [size, size], iconAnchor: [size / 2, size / 2] })
}

function dominantKind(events) {
    let counts = new Map()
    events.forEach((event) => counts.set(event.kind, (counts.get(event.kind) || 0) + 1))
    let best = KINDS[0]
    let bestCount = -1
    KINDS.forEach((kind) => {
        let count = counts.get(kind.key) || 0
        if (count > bestCount) {
            best = kind
            bestCount = count
        }
    })
    return best
}

// A line from where somebody was born to where they died. This is the thing a
// family map is actually for: a century of births and deaths in the same three
// villages is a line-less cluster, and the one ancestor who left draws a line
// across the map. Off by default - on a big tree it is a lot of ink, and the
// pins are what you read first.
function drawJourneys(layer, events, coords) {
    let byPerson = new Map()
    events.forEach((event) => {
        if (event.kind !== "birth" && event.kind !== "death") return

        let person = event.people[0]
        if (!person) return

        let entry = byPerson.get(person.id) || { person: person }
        entry[event.kind] = coords.get(event.key)
        byPerson.set(person.id, entry)
    })

    byPerson.forEach((entry) => {
        let { birth, death, person } = entry
        if (!birth || !death) return
        if (birth.lat === death.lat && birth.lon === death.lon) return

        L.polyline([[birth.lat, birth.lon], [death.lat, death.lon]], {
            className: "ft-map-journey",
            color: "#7c3aed",
            weight: 2,
            opacity: 0.55
        })
            .addTo(layer)
            .bindTooltip(`${person.name}: ${birth.label} → ${death.label}`)
    })
}

// --- Panel ------------------------------------------------------------------

// Tapping a pin lists what happened there, newest-known first. Same shape as
// the network map's city panel, because it answers the same question - it is
// the list behind the number on the bubble.
function buildPanel({ state, onSaved }) {
    let element = document.createElement("div")
    element.className = "ft-map-panel"
    element.hidden = true

    let head = document.createElement("div")
    head.className = "ft-map-panel-head"
    let title = document.createElement("h3")
    title.className = "ft-map-panel-title"
    let close = document.createElement("button")
    close.type = "button"
    close.className = "ft-map-panel-close"
    close.setAttribute("aria-label", "Close")
    close.innerHTML = '<i class="bi bi-x-lg"></i>'
    head.append(title, close)

    let body = document.createElement("div")
    body.className = "ft-map-panel-list"
    element.append(head, body)

    let url = null

    function closePanel() {
        element.hidden = true
    }
    close.addEventListener("click", closePanel)

    // --- Writing a correction ----------------------------------------------

    async function post(payload) {
        let response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify(payload)
        })
        return response.json().catch(() => ({ ok: false }))
    }

    async function reset(query) {
        let response = await fetch(url, {
            method: "DELETE",
            headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ query: query })
        })
        return response.json().catch(() => ({ ok: false }))
    }

    // "Where is this really?" - the same location typeahead the rest of the app
    // uses (/geo/suggest -> Geo::Places), so what gets saved is a place the
    // gazetteer already knows rather than a point somebody guessed at.
    function openEditor({ query, text, corrected }) {
        title.textContent = text
        body.replaceChildren()

        let intro = document.createElement("p")
        intro.className = "ft-map-editor-intro"
        intro.textContent = `Your records say "${text}". Search for where that actually is.`
        body.appendChild(intro)

        let input = document.createElement("input")
        input.type = "search"
        input.className = "form-control form-control-sm ft-map-editor-input"
        input.placeholder = "Town, region, country…"
        input.setAttribute("aria-label", `Where is ${text}?`)
        body.appendChild(input)

        let results = document.createElement("div")
        results.className = "ft-map-editor-results"
        body.appendChild(results)

        let status = document.createElement("p")
        status.className = "ft-map-editor-status"
        body.appendChild(status)

        if (corrected) {
            let undo = document.createElement("button")
            undo.type = "button"
            undo.className = "btn btn-link btn-sm ft-map-editor-undo"
            undo.textContent = "Use the gazetteer's answer again"
            undo.addEventListener("click", async () => {
                status.textContent = "Resetting…"
                let payload = await reset(query)
                if (!payload.ok) {
                    status.textContent = payload.error || "Could not reset that."
                    return
                }
                closePanel()
                onSaved()
            })
            body.appendChild(undo)
        }

        let timer = null
        input.addEventListener("input", () => {
            clearTimeout(timer)
            let value = input.value.trim()
            if (value.length < 3) {
                results.replaceChildren()
                return
            }
            timer = setTimeout(() => search(value), 250)
        })
        // Pre-fill with the original text: most of the time the register's
        // spelling is close enough that the first search is the right one.
        input.value = text
        setTimeout(() => {
            input.focus()
            input.select()
            search(text)
        }, 0)

        async function search(value) {
            status.textContent = "Searching…"
            let payload = await fetch(`/geo/suggest?q=${encodeURIComponent(value)}`, {
                headers: { Accept: "application/json" }, credentials: "same-origin"
            }).then((response) => response.json()).catch(() => null)
            status.textContent = ""
            let list = (payload && payload.suggestions) || []
            results.replaceChildren()
            if (!list.length) {
                status.textContent = "Nothing found for that. Try the modern name."
                return
            }
            list.forEach((suggestion) => {
                let row = document.createElement("button")
                row.type = "button"
                row.className = "ft-map-editor-result"
                row.textContent = suggestion.label
                row.addEventListener("click", async () => {
                    status.textContent = "Saving…"
                    let saved = await post({ query: query, place: suggestion })
                    if (!saved.ok) {
                        status.textContent = saved.error || "Could not save that."
                        return
                    }
                    closePanel()
                    onSaved()
                })
                results.appendChild(row)
            })
        }
    }

    // One pin can hold several spellings of the same village, and a correction
    // is about a STRING. With one there is nothing to ask; with several, which
    // one is wrong is a question only the reader can answer.
    function editPlace(place) {
        let spellings = place.spellings || [{ key: place.key, text: place.name }]
        if (spellings.length === 1) {
            openEditor({ query: spellings[0].key, text: spellings[0].text, corrected: place.corrected })
            return
        }

        title.textContent = place.label
        body.replaceChildren()
        let intro = document.createElement("p")
        intro.className = "ft-map-editor-intro"
        intro.textContent = "Your records spell this place several ways. Which one is in the wrong place?"
        body.appendChild(intro)
        spellings.forEach((spelling) => {
            let row = document.createElement("button")
            row.type = "button"
            row.className = "ft-map-editor-result"
            row.textContent = spelling.text
            row.addEventListener("click", () => openEditor({ query: spelling.key, text: spelling.text, corrected: place.corrected }))
            body.appendChild(row)
        })
        element.hidden = false
    }

    // Everything the gazetteer answered and could not place. Reachable from the
    // footer, because a place that draws nothing has nowhere else to be found.
    function openUnlocated() {
        title.textContent = `${state.unlocated.length} ${state.unlocated.length === 1 ? "place" : "places"} couldn't be located`
        body.replaceChildren()

        let intro = document.createElement("p")
        intro.className = "ft-map-editor-intro"
        intro.textContent = "Usually a historical spelling, or a cemetery the gazetteer files under its town. Say where each one is and it will stay put."
        body.appendChild(intro)

        state.unlocated.forEach((entry) => {
            let row = document.createElement("button")
            row.type = "button"
            row.className = "ft-map-event ft-map-unlocated"
            let mark = document.createElement("span")
            mark.className = "ft-map-event-mark ft-map-event-mark--empty"
            mark.innerHTML = '<i class="bi bi-question-lg" aria-hidden="true"></i>'
            let inner = document.createElement("span")
            inner.className = "ft-map-event-body"
            let name = document.createElement("span")
            name.className = "ft-map-event-name"
            name.textContent = entry.text
            let meta = document.createElement("span")
            meta.className = "ft-map-event-meta"
            meta.textContent = "Not on the map — say where"
            inner.append(name, meta)
            row.append(mark, inner)
            row.addEventListener("click", () => openEditor({ query: entry.query, text: entry.text, corrected: false }))
            body.appendChild(row)
        })
        element.hidden = false
    }

    return {
        element: element,
        close: closePanel,
        openUnlocated: openUnlocated,
        setUrl(value) { url = value },
        open(place, peopleMode) {
            title.textContent = place.coarse ? `${place.label} (region)` : place.label
            body.replaceChildren()

            // ONE COUNTRY, ONE PLACE. The map does not hide it or move it - it
            // says so and offers the two answers. See MapPlaces#flag_uncertain.
            if (place.uncertain && state.editable) {
                let note = document.createElement("div")
                note.className = "ft-map-panel-note"
                let text = document.createElement("span")
                text.textContent = "The only place here from this country — worth a check."
                let actions = document.createElement("span")
                actions.className = "ft-map-panel-note-actions"
                let yes = document.createElement("button")
                yes.type = "button"
                yes.className = "btn btn-sm btn-outline-secondary"
                yes.textContent = "That's right"
                yes.addEventListener("click", async () => {
                    yes.disabled = true
                    let saved = await post({ query: place.key, confirm: "true" })
                    if (!saved.ok) {
                        yes.disabled = false
                        return
                    }
                    closePanel()
                    onSaved()
                })
                let fix = document.createElement("button")
                fix.type = "button"
                fix.className = "btn btn-sm btn-outline-secondary"
                fix.textContent = "Fix…"
                fix.addEventListener("click", () => editPlace(place))
                actions.append(yes, fix)
                note.append(text, actions)
                body.appendChild(note)
            }

            sortEvents(place.events).forEach((event) => body.appendChild(peopleMode ? personRow(event) : eventRow(event)))

            if (state.editable) {
                let edit = document.createElement("button")
                edit.type = "button"
                edit.className = "btn btn-link btn-sm ft-map-panel-edit"
                edit.textContent = place.corrected ? "You placed this — change it" : "Not the right place?"
                edit.addEventListener("click", () => editPlace(place))
                body.appendChild(edit)
            }
            element.hidden = false
        }
    }
}

// Oldest first - a place's list read as a chronicle of it. Undated events sink
// to the bottom rather than claiming year zero.
function sortEvents(events) {
    return [...events].sort((a, b) => {
        if (a.year == null && b.year == null) return a.name.localeCompare(b.name)
        if (a.year == null) return 1
        if (b.year == null) return -1
        return a.year - b.year
    })
}

function eventRow(event) {
    let kind = KIND_BY_KEY.get(event.kind)
    let person = event.people[0]
    // Every other layout opens a card on click; this one has to as well, or a
    // pin is a dead end - you have found the village and cannot get to the man.
    let openable = person && personOpenable(person)
    let row = document.createElement(openable ? "a" : "div")
    row.className = "ft-map-event"
    if (openable) applyPersonLink(row, person)

    let mark = document.createElement("span")
    mark.className = "ft-map-event-mark"
    mark.style.background = kind.color
    let icon = document.createElement("i")
    icon.className = `bi ${kind.icon}`
    icon.setAttribute("aria-hidden", "true")
    mark.appendChild(icon)

    let body = document.createElement("span")
    body.className = "ft-map-event-body"
    let name = document.createElement("span")
    name.className = "ft-map-event-name"
    name.textContent = event.name
    let meta = document.createElement("span")
    meta.className = "ft-map-event-meta"
    meta.textContent = [kind.singular, event.year ? `· ${event.year}` : null].filter(Boolean).join(" ")
    body.append(name, meta)

    row.append(mark, body)
    return row
}

// A person, in people mode. The meta line says WHY they are on this dot: a
// person placed by their grave rather than their birth is a different claim,
// and a row that did not say so would read as "born here".
const BASIS_LABEL = { birth: "Born here", death: "Died here", burial: "Buried here" }

function personRow(entry) {
    let person = entry.people[0]
    let openable = person && personOpenable(person)
    let row = document.createElement(openable ? "a" : "div")
    row.className = "ft-map-event"
    if (openable) applyPersonLink(row, person)

    let mark = document.createElement("span")
    mark.className = "ft-map-event-mark"
    mark.style.background = PEOPLE_COLOR
    let icon = document.createElement("i")
    icon.className = "bi bi-person-fill"
    icon.setAttribute("aria-hidden", "true")
    mark.appendChild(icon)

    let body = document.createElement("span")
    body.className = "ft-map-event-body"
    let name = document.createElement("span")
    name.className = "ft-map-event-name"
    name.textContent = entry.name
    let meta = document.createElement("span")
    meta.className = "ft-map-event-meta"
    meta.textContent = [entry.lifespan, BASIS_LABEL[entry.kind]].filter(Boolean).join(" · ")
    body.append(name, meta)

    row.append(mark, body)
    return row
}

// --- Chrome -----------------------------------------------------------------

// The kind chips and the journeys toggle go on the shared toolbar row, where
// every other layout puts its filters - see contacts/_tree.
function renderFilters(slot, state, events, redraw) {
    let bar = document.createElement("div")
    bar.className = "ft-map-filters"
    let counts = new Map()
    events.forEach((event) => counts.set(event.kind, (counts.get(event.kind) || 0) + 1))

    // What the map is counting, first, because it changes what every other
    // control on the row means.
    //
    // A SPLIT CHIP, not two chips, because this row has a width budget: with
    // the assistant panel open it holds about five before it wraps, and the
    // event view already wants four kind chips and Journeys. Same control the
    // layout switcher uses next to it (contacts/_tree_view_switch), for the
    // same reason it does.
    let kindChips = []
    let modes = [
        { key: "people", label: "People", icon: "bi-person-fill", hint: "One mark per relative, where they are from" },
        { key: "events", label: "Events", icon: "bi-asterisk", hint: "Births, marriages, deaths and burials" }
    ]
    let split = document.createElement("div")
    split.className = "msg-chip-split"
    split.dataset.controller = "split-menu"
    split.dataset.splitMenuAnchorValue = "fixed"

    let current = () => modes.find((mode) => mode.key === state.mode) || modes[0]
    let main = document.createElement("button")
    main.type = "button"
    main.className = "msg-chip-split-main"
    main.setAttribute("aria-haspopup", "true")
    main.dataset.action = "split-menu#toggle"
    let caret = document.createElement("button")
    caret.type = "button"
    caret.className = "msg-chip-split-caret"
    caret.setAttribute("aria-haspopup", "true")
    caret.setAttribute("aria-expanded", "false")
    caret.setAttribute("aria-label", "Change what the map counts")
    caret.dataset.action = "split-menu#toggle"
    caret.dataset.splitMenuTarget = "caret"
    caret.innerHTML = '<i class="bi bi-chevron-down" aria-hidden="true"></i>'

    let menu = document.createElement("div")
    menu.className = "network-toolbar-menu-panel msg-chip-submenu"
    menu.dataset.splitMenuTarget = "menu"
    let menuTitle = document.createElement("div")
    menuTitle.className = "msg-chip-submenu-title"
    menuTitle.textContent = "Map shows"
    menu.appendChild(menuTitle)

    let items = modes.map((mode) => {
        let item = document.createElement("button")
        item.type = "button"
        item.className = "network-toolbar-menu-item"
        item.dataset.action = "split-menu#choose"
        item.title = mode.hint
        item.innerHTML = `<i class="bi ${mode.icon}" aria-hidden="true"></i>`
        let label = document.createElement("span")
        label.className = "msg-chip-submenu-label"
        label.textContent = mode.label
        let check = document.createElement("i")
        check.className = "bi bi-check2 msg-chip-submenu-check"
        check.setAttribute("aria-hidden", "true")
        item.append(label, check)
        item.addEventListener("click", () => {
            if (state.mode === mode.key) return

            state.mode = mode.key
            paintMode()
            // The kind chips belong to the event view - they have nothing to
            // narrow when every person is one mark.
            kindChips.forEach((el) => { el.hidden = state.mode !== "events" })
            redraw()
        })
        menu.appendChild(item)
        return { mode, item }
    })

    function paintMode() {
        let mode = current()
        main.innerHTML = `<i class="bi ${mode.icon}" aria-hidden="true"></i>`
        let label = document.createElement("span")
        label.textContent = mode.label
        main.appendChild(label)
        main.setAttribute("aria-label", `Change what the map counts (currently ${mode.label})`)
        items.forEach(({ mode: other, item }) => item.classList.toggle("is-active", other.key === state.mode))
    }
    paintMode()

    split.append(main, caret, menu)
    bar.appendChild(split)

    // The four kinds behind ONE chip, not four of them.
    //
    // This row has a width budget - about five chips before it wraps once the
    // assistant panel is open - and the toolbar it sits on already spends four
    // on layout, seat, people and share. /network answers the same problem the
    // same way, and so does the "Filter" chip two controls to the right: one
    // labelled dropdown per concern rather than a chip per value.
    //
    // The items do NOT carry split-menu#choose: these are toggles, and a menu
    // that shut on every click would make turning two kinds off a two-trip job.
    let kindMenu = null
    let available = KINDS.filter((kind) => {
        let count = counts.get(kind.key) || 0
        // A kind nobody in this tree recorded is not offered. A row of controls
        // that can only ever turn nothing off is noise.
        if (!count) state.kinds.delete(kind.key)
        return count > 0
    })

    if (available.length > 1) {
        kindMenu = document.createElement("div")
        kindMenu.className = "msg-chip-split"
        kindMenu.dataset.controller = "split-menu"
        kindMenu.dataset.splitMenuAnchorValue = "fixed"
        kindMenu.hidden = state.mode !== "events"

        let kindMain = document.createElement("button")
        kindMain.type = "button"
        kindMain.className = "msg-chip-split-main"
        kindMain.setAttribute("aria-haspopup", "true")
        kindMain.dataset.action = "split-menu#toggle"
        let kindCaret = document.createElement("button")
        kindCaret.type = "button"
        kindCaret.className = "msg-chip-split-caret"
        kindCaret.setAttribute("aria-haspopup", "true")
        kindCaret.setAttribute("aria-expanded", "false")
        kindCaret.setAttribute("aria-label", "Choose which events to map")
        kindCaret.dataset.action = "split-menu#toggle"
        kindCaret.dataset.splitMenuTarget = "caret"
        kindCaret.innerHTML = '<i class="bi bi-chevron-down" aria-hidden="true"></i>'

        let panel = document.createElement("div")
        panel.className = "network-toolbar-menu-panel msg-chip-submenu"
        panel.dataset.splitMenuTarget = "menu"
        let panelTitle = document.createElement("div")
        panelTitle.className = "msg-chip-submenu-title"
        panelTitle.textContent = "Which events"
        panel.appendChild(panelTitle)

        let rows = available.map((kind) => {
            let item = document.createElement("button")
            item.type = "button"
            item.className = "network-toolbar-menu-item"
            item.innerHTML = `<i class="bi ${kind.icon}" aria-hidden="true"></i>`
            let label = document.createElement("span")
            label.className = "msg-chip-submenu-label"
            label.textContent = kind.label
            let badge = document.createElement("span")
            badge.className = "msg-chip-count msg-chip-count--muted"
            badge.textContent = String(counts.get(kind.key) || 0)
            let check = document.createElement("i")
            check.className = "bi bi-check2 msg-chip-submenu-check"
            check.setAttribute("aria-hidden", "true")
            item.append(label, badge, check)
            item.addEventListener("click", (event) => {
                event.stopPropagation()
                let on = state.kinds.has(kind.key)
                // Never all of them off: an empty map with the control to fix
                // it right there still reads as "the map is broken".
                if (on && state.kinds.size === 1) return

                on ? state.kinds.delete(kind.key) : state.kinds.add(kind.key)
                paintKinds()
                redraw()
            })
            panel.appendChild(item)
            return { kind, item }
        })

        function paintKinds() {
            let on = available.filter((kind) => state.kinds.has(kind.key))
            kindMain.innerHTML = '<i class="bi bi-funnel" aria-hidden="true"></i>'
            let label = document.createElement("span")
            // Names the kinds while there is room for them, and counts them
            // once there is not - "Births, Deaths" says more than "2 of 4".
            label.textContent = on.length === available.length
                ? "All events"
                : (on.length <= 2 ? on.map((kind) => kind.label).join(", ") : `${on.length} of ${available.length}`)
            kindMain.appendChild(label)
            kindMain.setAttribute("aria-label", `Choose which events to map (currently ${label.textContent})`)
            rows.forEach(({ kind, item }) => {
                let active = state.kinds.has(kind.key)
                item.classList.toggle("is-active", active)
                item.setAttribute("aria-pressed", String(active))
            })
        }
        paintKinds()

        kindMenu.append(kindMain, kindCaret, panel)
        bar.appendChild(kindMenu)
        kindChips.push(kindMenu)
    }

    // Only where a line could be drawn - it needs somebody with both a
    // birthplace and a place of death. Offered in either mode: a journey is a
    // fact about a person, not about what the pins are counting.
    if (counts.get("birth") && counts.get("death")) {
        let journeys = document.createElement("button")
        journeys.type = "button"
        journeys.className = "msg-chip msg-chip--compact"
        journeys.setAttribute("aria-pressed", "false")
        journeys.title = "Draw a line from each birthplace to where that person died"
        let label = document.createElement("span")
        label.textContent = "Journeys"
        journeys.appendChild(label)
        journeys.addEventListener("click", () => {
            state.journeys = !state.journeys
            journeys.classList.toggle("is-active", state.journeys)
            journeys.setAttribute("aria-pressed", String(state.journeys))
            redraw()
        })
        bar.appendChild(journeys)
    }

    slot.appendChild(bar)
}

// What the map is and is not showing. The unmapped count is the important half:
// a historical spelling the gazetteer has never heard of is ordinary, and a
// reader who is not told will read the gap as the family never having been
// there.
function writeFooter(footer, { places, shown, state, publicView, totalEvents, totalPeople, placedPeople, onShowUnlocated }) {
    footer.replaceChildren()
    if (!state.loaded) {
        footer.textContent = "Locating places…"
        return
    }

    let mapped = places.reduce((sum, place) => sum + place.events.length, 0)
    // "persons" is what an `s` gives you, and it is not a word anybody wants on
    // a map of their own family.
    let unit = state.mode === "people"
        ? (mapped === 1 ? "person" : "people")
        : (mapped === 1 ? "event" : "events")

    let parts = [`${mapped} ${unit} in ${places.length} ${places.length === 1 ? "place" : "places"}`]
    let unmapped = shown.length - mapped
    if (unmapped > 0) parts.push(`${unmapped} couldn't be located`)
    if (state.pending > 0) parts.push(`${state.pending} still resolving — check back shortly`)
    if (state.mode === "events" && shown.length < totalEvents) parts.push(`${totalEvents - shown.length} hidden by the filter`)
    // WHY THE MAP LOOKS SPARSE. A 1,481-person tree with places on 88 of them
    // draws 88 dots, and without this line that reads as a broken map rather
    // than as a tree whose places are mostly still to be transcribed.
    if (state.mode === "people" && totalPeople > placedPeople) {
        let without = totalPeople - placedPeople
        parts.push(`${without} ${without === 1 ? "person has" : "people have"} ${publicView ? "no publicly shared place" : "no place recorded"}`)
    }
    footer.appendChild(document.createTextNode(parts.join(" · ")))

    // A place that draws nothing has nowhere else to be found, so the count is
    // also the way in to fixing it.
    if (state.editable && state.unlocated.length) {
        footer.appendChild(document.createTextNode(" · "))
        let fix = document.createElement("button")
        fix.type = "button"
        fix.className = "ft-map-footer-link"
        fix.textContent = `Place ${state.unlocated.length} the map couldn't find`
        fix.addEventListener("click", onShowUnlocated)
        footer.appendChild(fix)
    }
}

function emptyState(ctx) {
    let empty = document.createElement("div")
    empty.className = "ft-map-empty"
    let icon = document.createElement("span")
    icon.className = "ft-map-empty-icon"
    icon.innerHTML = '<i class="bi bi-geo-alt"></i>'
    let title = document.createElement("h2")
    title.className = "ft-map-empty-title"
    title.textContent = "No places recorded yet"
    let hint = document.createElement("p")
    hint.className = "text-muted small mb-0"
    hint.textContent = ctx.publicView
        ? "This tree doesn't record where anyone was born or died yet."
        : "Add a birthplace or a place of death on someone's card — a village name is enough — and they'll appear here."
    empty.append(icon, title, hint)
    return empty
}
